import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { currentLeague, leagueUrl } from '../../core/league/league';
import { getEngine } from '../../core/sync/appEngine';
import { useAppState, useRunSyncEngine } from '../../core/sync/useAppState';
import { selectAppData, type AppData } from '../appData';
import { BottomTabs } from './BottomTabs';
import { Header } from './Header';
import { TabErrorBoundary } from './TabErrorBoundary';
import { useApplyTheme } from './theme';
import { useHashRoute, type Route } from './useHashRoute';
import { useRoundGuard } from './useRoundGuard';
import TodayTab from '../today/TodayTab';

const UpcomingTab = lazy(() => import('../upcoming/UpcomingTab'));
const SeasonTab = lazy(() => import('../season/SeasonTab'));
const LeagueTab = lazy(() => import('../league/LeagueTab'));

function TabContent({ route, data }: { route: Route; data: AppData }) {
    switch (route) {
        case 'tanaan': return <TodayTab data={data} />;
        case 'tulevat': return <UpcomingTab data={data} />;
        case 'kausi': return <SeasonTab data={data} />;
        case 'liiga': return <LeagueTab data={data} />;
    }
}

// The app shell: header, the current tab and the bottom tabs. Until the data
// has loaded the tabs are replaced by a loading note, or by a retry button if
// the very first load failed (later failures keep showing the last good data).
export default function App() {
    const { t } = useTranslation();
    const state = useAppState();
    const route = useHashRoute();
    useRunSyncEngine();
    useApplyTheme();

    const league = currentLeague();
    const data = selectAppData(state, league);
    useRoundGuard(data, route);
    const failed = data === undefined && [state.players, state.points, state.scores, state.standings]
        .some(resource => resource.status === 'error');

    let content;
    if (data === undefined && state.unavailable) {
        content = (
            <div className="state-message" role="alert" data-testid="league-unavailable">
                <p>{t('state_leagueUnavailable', { league })}</p>
                <a href={leagueUrl('NHL', '')}>{t('state_toNhl')}</a>
            </div>
        );
    } else if (data === undefined) {
        content = failed
            ? (
                <div className="state-message" role="alert">
                    <p>{t('state_noData')}</p>
                    <button type="button" onClick={() => getEngine().retryNow()}>{t('state_retry')}</button>
                </div>
            )
            : <div className="state-message" role="status">{t('state_loading')}</div>;
    } else if (data.players.length === 0) {
        content = <div className="state-message" role="status">{t('state_noPlayers')}</div>;
    } else {
        content = (
            <TabErrorBoundary key={`${route}-${state.country}`}>
                <Suspense fallback={<div className="state-message" role="status">{t('state_loading')}</div>}>
                    <TabContent route={route} data={data} />
                </Suspense>
            </TabErrorBoundary>
        );
    }

    return (
        <div className="app">
            <Header state={state} league={league} />
            <main className="app-main">{content}</main>
            <BottomTabs route={route} />
        </div>
    );
}
